import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { subDays } from "date-fns";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Grid, IconButton, LinearProgress, Tooltip, Typography
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import ProzoDataGrid from "../../common/ProzoDataGrid";
import RHFSelectField from "../../common/hook-form/RHFSelectField";
import { escalationArray, parseDateAndTime } from "../../helpers/UtilityHelper";
import { RHFTextField, FormProvider, RHFFileField } from "../../common/hook-form";
import DateRangePicker from "../DateRangePicker";
import SideDrawer from "../SideDrawer";
import UploadIcon from '@mui/icons-material/Upload';
import MUIModal from "../MuiModal";
import Loader from "../../common/Loader";
import jwt_decode from "jwt-decode";
import { apiConstants, patchGeneric, post, postGeneric } from "../../common/constants";
import { formatDate1 } from "../../common/util";
import _ from "lodash";
import CreateTicket from "./CreateTicket";
import { useSnackbar } from "notistack";
import { useGetAllActiveMerchantsIdAndNameCachedQuery, useGetAllCourierPartnersCachedQuery } from "../../redux/commonApiCalls";
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadSampleFile from "../DownloadSampleFile";
import * as XLSX from 'xlsx/xlsx.mjs';
import { getToken } from "../../helpers/ReduxHelpers";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import { FieldLabel, FilterField, FilterDateField } from "../../common/FilterBar";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import CopyIcon from "../../common/CopyIcon";

/* ── Status chip color map ── */
const getStatusStyle = (id) => {
    const m = {
        2:  { bg: '#fff3e0', color: '#e65100', border: '#ffe0b2' },   // Open
        3:  { bg: '#fff8e1', color: '#f57f17', border: '#ffecb3' },   // Pending
        4:  { bg: '#f5f5f5', color: '#616161', border: '#e0e0e0' },   // Closed
        5:  { bg: '#e8f5e9', color: '#2e7d32', border: '#c8e6c9' },   // Resolved
        6:  { bg: '#e3f2fd', color: '#1565c0', border: '#bbdefb' },   // Waiting on Customer
        9:  { bg: '#f3e5f5', color: '#7b1fa2', border: '#e1bee7' },   // Waiting on Third Party
        10: { bg: '#fce4ec', color: '#c62828', border: '#f8bbd0' },   // Escalated
    };
    return m[id] || { bg: '#fce4ec', color: '#c62828', border: '#f8bbd0' };
};

/* ══════════════════════════════════════════════════════════════════ */

const TicketList = ({ reload, status, setIsLoading, isLoading, setOpenCount, setCloseCount, statusList }) => {
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(30);
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [resData, setresData] = useState([]);
    const [openBulkModal, setOpenBulkModal] = useState(false);
    const [openBulkReplyModal, setOpenBulkReplyModal] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);
    const [filterChange, setFiltersChange] = useState(0);
    const [currentId, setCurrentId] = useState();
    const [sampleName, setSampleName] = useState();
    const [ticketId, setTicketId] = useState()
    const [itemModal, setItemModal] = useState(false);
    const [itemModalData, setItemModalData] = useState();
    const userToken = jwt_decode(getToken());
    const merchantId = userToken && userToken ? userToken : "";
    const { userInfo } = useSelector((state) => state.user);

    const [bulkResolveResults, setBulkResolveResults] = useState([]);
    const [isBulkResolveProcessing, setIsBulkResolveProcessing] = useState(false);
    const [bulkReplyResults, setBulkReplyResults] = useState([]);
    const [isBulkReplyProcessing, setIsBulkReplyProcessing] = useState(false);
    const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0 });
    const cancelBulkRef = useRef(false);

    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
    const andfilter = [{ "field": "is_deleted", "operator": "eq", "value": '0' }];
    let status_list = [2, 3, 6, 9, 10];
    if (status === 'closed') {
        status_list = [4, 5];
    }
    const filter = {
        "andfilter": andfilter,
        "offset": 0,
        "limit": 1000,
        status_list: status_list,
        "skipCount": true,
        "includes": "id,name,cpAccountCode,prozoId,supportedShipmentType,isBlended"
    }
    const { data: courier } = useGetAllCourierPartnersCachedQuery(filter);

    const initialDateRange = [{
        endDate: new Date(),
        startDate: subDays(new Date(), 15),
        key: 'selection'
    }];
    const [dateRange, setDateRange] = useState(initialDateRange)
    const getFormattedDates = (range) => ({
        start_date: formatDate1(range[0].startDate, "yyyy-MM-dd"),
        end_date: formatDate1(range[0].endDate, "yyyy-MM-dd"),
    });
    const [andFilters, setAndFilters] = useState(getFormattedDates(initialDateRange))
    const { enqueueSnackbar } = useSnackbar();

    /* ── Column definitions ── */
    const columns = [
        {
            field: 'id', headerAlign: "left", headerName: 'Ticket ID', width: 100,
            align: 'left',
            renderCell: ({ row }) => (
                <Typography
                    component="a"
                    href={`/tools/escalation/view?awb=${row.custom_fields?.cf_awb_number387695}&ticketId=${row.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        color: '#1565c0', fontWeight: 700, fontSize: '0.8rem',
                        textDecoration: 'none',
                        '&:hover': { textDecoration: 'underline', color: '#1565c0' },
                    }}
                >
                    #{row?.id}
                </Typography>
            ),
        },
        {
            field: 'awb', headerAlign: "left", width: 160, headerName: 'AWB(s)',
            align: 'left',
            renderCell: ({ row }) => {
                const awb = row.custom_fields?.cf_awb_number387695;
                if (!awb) return <Typography variant="body2" color="text.disabled">-</Typography>;
                return (
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography
                                component="a"
                                href={`/order-detail/${awb}`}
                                target="_blank" rel="noopener noreferrer"
                                sx={{
                                    color: '#1565c0', fontSize: '0.8rem', fontWeight: 500,
                                    textDecoration: 'none', display: 'block',
                                    '&:hover': { textDecoration: 'underline', color: '#1565c0' },
                                }}
                            >
                                {awb}
                            </Typography>
                            <CopyIcon text={awb} />
                        </Box>
                        <Typography
                            component="span"
                            onClick={() => handleViewModalOpen(row)}
                            sx={{
                                color: '#d32f2f', fontSize: '0.7rem', fontWeight: 600,
                                cursor: 'pointer', mt: 0.25, display: 'inline-block',
                                '&:hover': { textDecoration: 'underline' },
                            }}
                        >
                            View Details
                        </Typography>
                    </Box>
                )
            },
        },
        {
            field: 'merchant', headerAlign: "left", width: 170, headerName: 'Merchant',
            align: 'left',
            renderCell: ({ row }) => {
                const mid = row?.custom_fields?.cf_merchant_id;
                const merchant = merchants?.find((m) => m.id === mid);
                return (
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }} noWrap>
                        {merchant ? merchant.name : '-'}
                    </Typography>
                );
            },
        },
        {
            field: 'courier', headerAlign: "left", width: 170, headerName: 'Courier',
            align: 'left',
            renderCell: ({ row }) => {
                const cpId = row?.custom_fields?.cf_cp_id;
                const val = courier?.find((c) => c.prozoId === cpId);
                return (
                    <Typography variant="body2" sx={{ fontSize: '0.82rem' }} noWrap>
                        {val?.cpAccountCode || "-"}
                    </Typography>
                );
            },
        },
        {
            field: 'createdBy', headerAlign: "left", width: 170, headerName: 'Created By',
            align: 'left',
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={{ fontSize: '0.82rem' }} noWrap>
                    {row.custom_fields?.cf_user_name || "-"}
                </Typography>
            ),
        },
        {
            field: 'category', headerAlign: "left", width: 180, headerName: 'Category',
            align: 'left',
            renderCell: ({ row }) => {
                const cat = row.custom_fields?.cf_category;
                return cat ? (
                    <Tooltip title={cat} arrow>
                        <Chip label={cat} size="small" sx={{
                            height: 24, maxWidth: 160, fontSize: '0.72rem', fontWeight: 600,
                            backgroundColor: '#f0f4ff', color: '#3d5afe', border: '1px solid #c5cae9',
                            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                        }} />
                    </Tooltip>
                ) : '-';
            },
        },
        {
            field: 'subcategory', headerAlign: "left", width: 200, headerName: 'SubCategory',
            align: 'left',
            renderCell: ({ row }) => {
                const sub = row.custom_fields?.cf_sub_category;
                return sub ? (
                    <Tooltip title={sub} arrow>
                        <Chip label={sub} size="small" sx={{
                            height: 24, maxWidth: 180, fontSize: '0.72rem', fontWeight: 500,
                            backgroundColor: '#f5f5f5', color: '#616161', border: '1px solid #e0e0e0',
                            '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                        }} />
                    </Tooltip>
                ) : '-';
            },
        },
        {
            field: 'fr_due_by', headerAlign: "left", width: 145, headerName: 'First Response Due',
            align: 'left',
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={{ fontSize: '0.78rem', color: '#637381' }}>
                    {parseDateAndTime(row?.fr_due_by)}
                </Typography>
            ),
        },
        {
            field: 'due_by', headerAlign: "left", width: 145, headerName: 'Resolution Due By',
            align: 'left',
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={{ fontSize: '0.78rem', color: '#637381' }}>
                    {parseDateAndTime(row?.due_by)}
                </Typography>
            ),
        },
        {
            field: 'created_at', headerAlign: "left", width: 145, headerName: 'Created Date',
            align: 'left',
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={{ fontSize: '0.78rem', color: '#637381' }}>
                    {parseDateAndTime(row?.created_at)}
                </Typography>
            ),
        },
        {
            field: 'updated_at', headerAlign: "left", width: 145, headerName: 'Last Updated',
            align: 'left',
            renderCell: ({ row }) => (
                <Typography variant="body2" sx={{ fontSize: '0.78rem', color: '#637381' }}>
                    {parseDateAndTime(row?.updated_at)}
                </Typography>
            ),
        },
        {
            field: 'status', headerAlign: "left", width: 190, headerName: 'Status',
            align: 'left',
            renderCell: ({ row }) => {
                const st = statusList?.find((elem) => elem.id === row?.status);
                const style = getStatusStyle(row?.status);
                return st ? (
                    <Tooltip title={st.label} arrow>
                        <Chip
                            label={st.label}
                            size="small"
                            sx={{
                                height: 26, maxWidth: 170, fontSize: '0.72rem', fontWeight: 700,
                                backgroundColor: style.bg, color: style.color,
                                border: `1px solid ${style.border}`,
                                '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                            }}
                        />
                    </Tooltip>
                ) : '-';
            },
        },
        {
            field: 'action', headerAlign: "center", width: 120, headerName: 'Actions',
            align: 'center', sortable: false,
            renderCell: ({ row }) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Tooltip title="View" arrow>
                        <IconButton
                            size="small"
                            onClick={() => view(row?.id, row.custom_fields?.cf_awb_number387695)}
                            sx={{
                                color: '#1565c0', backgroundColor: '#e3f2fd',
                                '&:hover': { backgroundColor: '#bbdefb' },
                                width: 30, height: 30,
                            }}
                        >
                            <VisibilityIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={row.status !== 5 ? "Close" : "Already closed"} arrow>
                        <span>
                            <IconButton
                                size="small"
                                disabled={row.status === 5}
                                onClick={() => toggleDelete(row?.id, "delete")}
                                sx={{
                                    color: '#d32f2f', backgroundColor: '#fce4ec',
                                    '&:hover': { backgroundColor: '#f8bbd0' },
                                    '&.Mui-disabled': { backgroundColor: '#f5f5f5', color: '#bdbdbd' },
                                    width: 30, height: 30,
                                }}
                            >
                                <CancelIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={row.status === 5 ? "Reopen" : "Not closed"} arrow>
                        <span>
                            <IconButton
                                size="small"
                                disabled={row.status !== 5}
                                onClick={() => toggleDelete(row?.id, "reopen")}
                                sx={{
                                    color: '#2e7d32', backgroundColor: '#e8f5e9',
                                    '&:hover': { backgroundColor: '#c8e6c9' },
                                    '&.Mui-disabled': { backgroundColor: '#f5f5f5', color: '#bdbdbd' },
                                    width: 30, height: 30,
                                }}
                            >
                                <CheckCircleIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    /* ── Form & filter logic (unchanged) ── */
    const methods = useForm({
        defaultValues: {
            status: '',
            category: '',
            subcategory: '',
            awb: '',
            merchant: '',
            courier: '',
            ticketId: '',
            file: null
        }
    });
    const { handleSubmit, reset } = methods;
    const data = escalationArray.filter((val) => val.categoryTitle);
    const categoryOptions = _.orderBy(data, "name").map((val) => ({
        value: val.categoryTitle,
        label: val.categoryTitle,
    }));

    const subCategoriesArray = escalationArray.flatMap(category =>
        category.subCategories.map(subCategory => ({
            name: subCategory.name,
            subCatTitle: subCategory.subCatTitle
        }))
    );

    const subcategoryOptions =
        subCategoriesArray?.map((subCat) => ({
            value: subCat.subCatTitle,
            label: subCat.subCatTitle,
        }));

    const onSubmit = (filters) => {
        let payload = {
            start_date: formatDate1(dateRange[0].startDate, "yyyy-MM-dd"),
            end_date: formatDate1(dateRange[0].endDate, "yyyy-MM-dd"),
        }
        if (filters.status) {
            payload.status_list = [filters.status]
        }
        if (filters.category) {
            payload.category_list = [filters.category]
        }
        if (filters.subcategory) {
            payload.subcategory_list = [filters.subcategory]
        }
        if (filters.awb) {
            payload.awbnumber_list = [filters.awb]
        }
        if (filters.merchant) {
            payload.merchantOid = filters.merchant
        }
        if (filters.courier) {
            payload.cpIdList = [filters.courier]
        }
        if (filters.ticketId) {
            navigate(`/tools/escalation/view?awb=${null}&ticketId=${filters.ticketId}`)
        } else {
            setAndFilters(payload)
        }
    }

    useEffect(() => {
        setIsLoading(true);
        const fetchData = async () => {
            let request = {
                ...andFilters,
                status_list: andFilters.status_list || status_list,
                page: page + 1,
            }
            if (andFilters.category_list) {
                request.category_list = andFilters.category_list
            }
            try {
                const response = await postGeneric(`${apiConstants.FILTER_FRESHDESK}`, request);
                const parsedRes = JSON.parse(response.data?.responseObj);
                setresData(parsedRes);
                if (status === 'open') {
                    setOpenCount(parsedRes?.total)
                } else {
                    setCloseCount(parsedRes?.total)
                }
                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
                console.error(error);
                enqueueSnackbar("Some error occurred in Loading!", { variant: 'error' })
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [andFilters, reload, page, filterChange, userInfo]);

    const handleReset = () => {
        reset({
            status: '',
            category: '',
            subcategory: ''
        });
        setDateRange(initialDateRange)
        setAndFilters(getFormattedDates(initialDateRange));
    }

    const navigate = useNavigate()
    const view = (id, awb) => {
        navigate(`/tools/escalation/view?awb=${awb}&ticketId=${id}`)
    }
    const [open, setOpen] = useState(false)

    const handleClose = () => {
        setOpen(false)
    }
    const handleOpen = () => {
        setOpen(true)
        setSidebarOpen(false)
    }
    const handleViewModalOpen = (row) => {
        setItemModal(true)
        setItemModalData(row?.custom_fields?.cf_awb_number387695)
    }
    const handleViewModalClose = () => {
        setItemModal(false)
    }

    const handleCloseDelete = () => {
        setDeleteConfirm(false)
    }
    const [operation, setOperation] = useState("")
    const toggleDelete = (id, val) => {
        setCurrentId(id);
        setDeleteConfirm(true);
        setOperation(val)
    };
    const closeticket = (id) => {
        let payload = {
            "custom_fields": {
                "cf_closed_by_name": merchantId?.name,
                "cf_closed_by_email": merchantId?.fullname
            }
        }
        patchGeneric(`${apiConstants.CLOSE_FRESHDESK}/${id}`, payload).then((res) => {
            setFiltersChange(filterChange + 1);
            enqueueSnackbar("Success!", { variant: 'success' })
            handleCloseDelete()
        }).catch((err) => {
            console.log(err);
            enqueueSnackbar("Some error occurred!", { variant: 'error' })
            handleCloseDelete()
        })
    }

    const reopenticket = (id) => {
        let payload = {
            "custom_fields": {
                "cf_reopened_by_name": merchantId?.name,
                "cf_reopened_by_email": merchantId?.fullname
            }
        }
        patchGeneric(`${apiConstants.REOPEN_FRESHDESK}/${id}`, payload).then((res) => {
            setFiltersChange(filterChange + 1);
            enqueueSnackbar("Reopen Successfull", { variant: 'success' })
            handleCloseDelete()
        }).catch((err) => {
            console.log(err);
            enqueueSnackbar("Some error occurred!", { variant: 'error' })
            handleCloseDelete()
        })
    }
    const handleConfirm = (currentId) => {
        if (operation === "delete") {
            closeticket(currentId)
        } else {
            reopenticket(currentId)
        }
    }

    /* ── Throttled bulk sender ─────────────────────────────────────────────────
       Freshdesk caps API calls at ~500/min per account, and every sheet row is one
       call. Firing them all at once (old Promise.all) burst past the cap and rows
       beyond ~500 failed with 429. We now send in small batches with a gap so the
       running rate stays well under the cap, retry 429s using the Retry-After
       header, report live progress, and allow a user-initiated stop. A >500-row
       sheet therefore completes over a few minutes instead of failing. */
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
    const BULK_CHUNK = 6;            // concurrent calls per batch
    const BULK_BATCH_GAP_MS = 800;   // gap between batches → ~450 calls/min (headroom under 500)
    const MAX_429_RETRIES = 4;

    const parseSheet1 = (binary) => {
        const workbook = XLSX.read(binary, { type: 'binary', cellDates: true });
        let rows = [];
        workbook.SheetNames.forEach((sheetName) => {
            const roa = XLSX.utils.sheet_to_row_object_array(workbook.Sheets[sheetName], { header: 0, raw: false, dateNF: 'yyyy-mm-dd' });
            if (sheetName === 'Sheet1' && roa.length > 0) rows = roa;
        });
        return rows;
    };

    // Batched runner with cooperative cancel + live progress.
    const runBulk = async (rows, worker, setResults) => {
        const results = [];
        setBulkProgress({ done: 0, total: rows.length });
        for (let i = 0; i < rows.length; i += BULK_CHUNK) {
            if (cancelBulkRef.current) {
                rows.slice(i).forEach((row) => results.push({ ticketId: row.ticket_id, status: 'Cancelled', message: 'Stopped by user' }));
                break;
            }
            const batch = rows.slice(i, i + BULK_CHUNK);
            const batchResults = await Promise.all(batch.map((row) => worker(row)));
            results.push(...batchResults);
            setResults([...results]);
            setBulkProgress({ done: results.length, total: rows.length });
            if (i + BULK_CHUNK < rows.length && !cancelBulkRef.current) await sleep(BULK_BATCH_GAP_MS);
        }
        return results;
    };

    const closeTicketRow = async (row, attempt = 0) => {
        const status = statusList?.find((elem) => elem?.label?.trim()?.toLowerCase() === row?.status?.trim()?.toLowerCase());
        const payload = { "custom_fields": { "cf_closed_by_name": merchantId?.name, "cf_closed_by_email": merchantId?.fullname } };
        try {
            const res = await patchGeneric(`${apiConstants.CLOSE_FRESHDESK}/${row.ticket_id}?resolvedStatus=${status?.id}`, payload);
            if (res && res.status === 200 && res.data?.status !== 'ERROR') {
                return { ticketId: row.ticket_id, status: 'Success', message: 'Ticket closed successfully' };
            }
            return { ticketId: row.ticket_id, status: 'Error', message: res.data?.responseObj || 'Error closing ticket' };
        } catch (err) {
            if (err?.response?.status === 429 && attempt < MAX_429_RETRIES) {
                const retryAfter = Number(err?.response?.headers?.['retry-after']) || 5;
                await sleep(retryAfter * 1000);
                return closeTicketRow(row, attempt + 1);
            }
            return { ticketId: row.ticket_id, status: 'Failed', message: err?.response?.status === 429 ? 'Rate limit hit — retry later' : 'Error closing ticket' };
        }
    };

    const replyTicketRow = async (row, attempt = 0) => {
        const formData = new FormData();
        formData.append('body', `<h4>Prozo : ${merchantId.fullname} / ${merchantId.name}</h4><p>${row.reply + "|tikprzautoProd"}</p>`);
        formData.append('cc_emails', merchantId.name);
        formData.append('is_private ', true);
        try {
            const res = await postGeneric(`${apiConstants.REPLY_FRESHDESK}/${row.ticket_id}`, formData);
            if (res && res.status === 200 && res.data?.status !== 'ERROR') {
                return { ticketId: row.ticket_id, status: 'Success', message: 'Reply posted successfully' };
            }
            return { ticketId: row.ticket_id, status: 'Error', message: 'Error posting reply ' + res.data?.errorMsg };
        } catch (err) {
            if (err?.response?.status === 429 && attempt < MAX_429_RETRIES) {
                const retryAfter = Number(err?.response?.headers?.['retry-after']) || 5;
                await sleep(retryAfter * 1000);
                return replyTicketRow(row, attempt + 1);
            }
            return { ticketId: row.ticket_id, status: 'Failed', message: err?.response?.status === 429 ? 'Rate limit hit — retry later' : 'Error posting reply' };
        }
    };

    const bulkResolveTickets = async (data) => {
        const file = data.file?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async function (e) {
            cancelBulkRef.current = false;
            setBulkResolveResults([]);
            setIsBulkResolveProcessing(true);
            try {
                const rows = parseSheet1(e.target.result);
                if (!rows.length) { enqueueSnackbar('No rows found in Sheet1.', { variant: 'warning' }); return; }
                const results = await runBulk(rows, closeTicketRow, setBulkResolveResults);
                const ok = results.filter((r) => r.status === 'Success').length;
                enqueueSnackbar(`Bulk resolve finished — ${ok}/${results.length} closed.`, { variant: ok === results.length ? 'success' : 'warning' });
            } catch (err) {
                console.error(err);
                enqueueSnackbar('Bulk resolve failed to process the file.', { variant: 'error' });
            } finally {
                setIsBulkResolveProcessing(false);
                cancelBulkRef.current = false;
            }
        };
        reader.onerror = () => { setIsBulkResolveProcessing(false); enqueueSnackbar('Could not read the file.', { variant: 'error' }); };
        reader.readAsBinaryString(file);
    }

    const bulkUploadReply = async (data) => {
        const file = data.file?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async function (e) {
            cancelBulkRef.current = false;
            setBulkReplyResults([]);
            setIsBulkReplyProcessing(true);
            try {
                const rows = parseSheet1(e.target.result);
                if (!rows.length) { enqueueSnackbar('No rows found in Sheet1.', { variant: 'warning' }); return; }
                const results = await runBulk(rows, replyTicketRow, setBulkReplyResults);
                const ok = results.filter((r) => r.status === 'Success').length;
                enqueueSnackbar(`Bulk comments finished — ${ok}/${results.length} posted.`, { variant: ok === results.length ? 'success' : 'warning' });
            } catch (err) {
                console.error(err);
                enqueueSnackbar('Bulk comment upload failed to process the file.', { variant: 'error' });
            } finally {
                setIsBulkReplyProcessing(false);
                cancelBulkRef.current = false;
            }
        };
        reader.onerror = () => { setIsBulkReplyProcessing(false); enqueueSnackbar('Could not read the file.', { variant: 'error' }); };
        reader.readAsBinaryString(file);
    }

    /* ── Navigation guard while a bulk upload runs ─────────────────────────────
       react-router v6.3 (BrowserRouter) has no useBlocker/Prompt, so we guard with
       plain browser APIs instead of the router:
         • beforeunload → native confirm on reload / tab-close / external URL
         • capture-phase click → block in-app sidebar/menu <a> navigation (fires
           before React Router's Link onClick), warn instead
         • popstate → re-push the same URL to neutralise the Back button, warn
       All are attached only while processing and torn down when it ends. */
    useEffect(() => {
        const processing = isBulkResolveProcessing || isBulkReplyProcessing;
        if (!processing) return undefined;
        const WARN = 'A bulk upload is running. Please wait for it to finish, or use “Stop upload” to cancel.';

        const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
        const onClickCapture = (e) => {
            const target = e.target;
            const anchor = target && target.closest ? target.closest('a[href]') : null;
            if (!anchor) return;
            if (anchor.closest('.MuiDialog-root')) return; // allow links inside the bulk modal
            e.preventDefault();
            e.stopPropagation();
            enqueueSnackbar(WARN, { variant: 'warning' });
        };
        const onPopState = () => {
            window.history.pushState(null, '', window.location.href);
            enqueueSnackbar(WARN, { variant: 'warning' });
        };

        window.addEventListener('beforeunload', onBeforeUnload);
        document.addEventListener('click', onClickCapture, true);
        window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', onPopState);

        return () => {
            window.removeEventListener('beforeunload', onBeforeUnload);
            document.removeEventListener('click', onClickCapture, true);
            window.removeEventListener('popstate', onPopState);
        };
    }, [isBulkResolveProcessing, isBulkReplyProcessing, enqueueSnackbar]);

    /* ══════════════════════════════════════════════════════════════ */
    /*                          R E N D E R                          */
    /* ══════════════════════════════════════════════════════════════ */
    return (
        <>
            {isLoading && <Loader />}

            {/* ── Confirm Dialog ── */}
            <Dialog
                open={deleteConfirm}
                onClose={handleCloseDelete}
                PaperProps={{ sx: { borderRadius: 3, minWidth: 400, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', pb: 0.5 }}>
                    {operation === "delete" ? "Close Ticket" : "Reopen Ticket"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: '#637381', mt: 1 }}>
                        {operation === "delete"
                            ? "Are you sure you want to close this ticket?"
                            : "Are you sure you want to reopen this ticket?"}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                    <Button
                        onClick={handleCloseDelete}
                        variant="outlined" color="inherit"
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleConfirm(currentId)}
                        variant="contained"
                        color={operation === "delete" ? "error" : "success"}
                        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                    >
                        {operation === "delete" ? "Yes, Close" : "Yes, Reopen"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── Success Modal ── */}
            <MUIModal open={open} maxWidth="sm" handleClose={handleClose}>
                <Box sx={{ textAlign: 'center', py: 3 }}>
                    <Box sx={{
                        width: 64, height: 64, borderRadius: '50%',
                        backgroundColor: '#e8f5e9',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mx: 'auto', mb: 2,
                    }}>
                        <CheckCircleIcon sx={{ fontSize: 36, color: '#2e7d32' }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
                        Ticket Created Successfully
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                        Updates may take up to a minute to appear on your screen. Kindly avoid making duplicate requests.
                    </Typography>
                    <Chip
                        label={`Ticket ID: ${ticketId}`}
                        sx={{
                            fontWeight: 700, fontSize: '0.9rem', height: 34,
                            backgroundColor: '#e3f2fd', color: '#1565c0',
                            border: '1px solid #bbdefb',
                        }}
                    />
                </Box>
            </MUIModal>

            {/* ── AWB Details Modal ── */}
            <MUIModal
                open={itemModal}
                handleClose={handleViewModalClose}
                maxWidth="lg"
                title={"Details"}
                children={<ViewAwbDetails id={itemModalData} />}
            />

            {/* ── Create Ticket Drawer ── */}
            <SideDrawer
                title='Create Ticket'
                subtitle='Provide details to raise a support case'
                icon={<ConfirmationNumberOutlinedIcon sx={{ fontSize: 22 }} />}
                open={sidebarOpen ? true : false}
                setOpen={() => setSidebarOpen(false)}
                children={
                    <CreateTicket
                        setFiltersChange={setFiltersChange}
                        handleOpen={handleOpen}
                        setTicketId={setTicketId}
                        statusList={statusList}
                    />
                }
            />

            {/* ══════════════ ACTION BAR ══════════════ */}
            <Box sx={{
                mb: 2, display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
            }}>
                <Button
                    variant="contained"
                    onClick={() => setSidebarOpen(true)}
                    startIcon={<AddCircleOutlineIcon />}
                    sx={{
                        borderRadius: 2.5, textTransform: 'none', fontWeight: 600, px: 2.5,
                        background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                        boxShadow: '0 4px 12px rgba(21,101,192,0.25)',
                        '&:hover': { boxShadow: '0 6px 16px rgba(21,101,192,0.35)' },
                    }}
                >
                    Create Ticket
                </Button>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Tooltip title="Please click 'Export' in Freshdesk to export tickets">
                        <Button
                            variant="outlined" size="small"
                            startIcon={<FileDownloadIcon sx={{ fontSize: 18 }} />}
                            onClick={() => {
                                window.open('https://support.prozo.com/a/tickets/filters/search?label=Open%20tickets%20in%20proship_ems&orderBy=created_at&orderType=desc&q[]=group%3A%5B88000150233%5D&ref=all_tickets', '_blank');
                            }}
                            sx={{
                                borderRadius: 2, textTransform: 'none', fontWeight: 600,
                                borderColor: '#e0e0e0', color: '#637381',
                                '&:hover': { borderColor: '#bdbdbd', backgroundColor: '#fafafa' },
                            }}
                        >
                            Export
                        </Button>
                    </Tooltip>
                    {status === 'open' && (
                        <>
                            <Button
                                variant="outlined" size="small"
                                onClick={() => {
                                    setOpenBulkModal(true);
                                    setSampleName('EMS_RESOLVE_TICKETS_2')
                                }}
                                sx={{
                                    borderRadius: 2, textTransform: 'none', fontWeight: 600,
                                    borderColor: '#c8e6c9', color: '#2e7d32',
                                    '&:hover': { borderColor: '#a5d6a7', backgroundColor: '#f1f8e9' },
                                }}
                            >
                                Bulk Resolve
                            </Button>
                            <Button
                                variant="outlined" size="small"
                                onClick={() => {
                                    setOpenBulkReplyModal(true);
                                    setSampleName('EMS_ADD_COMMENTS')
                                }}
                                sx={{
                                    borderRadius: 2, textTransform: 'none', fontWeight: 600,
                                    borderColor: '#c8e6c9', color: '#2e7d32',
                                    '&:hover': { borderColor: '#a5d6a7', backgroundColor: '#f1f8e9' },
                                }}
                            >
                                Bulk Comments
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {/* ══════════════ FILTER SECTION (matches Forward Shipments) ══════════════ */}
            <Card sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', borderRadius: '8px' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-end' }}>

                            <FilterDateField>
                                <FieldLabel>Date Range</FieldLabel>
                                <DateRangePicker
                                    defaultRange={dateRange}
                                    maxDate={new Date()}
                                    showTextBox={true}
                                    label=""
                                    placeholder="Select Date Range"
                                    size="small"
                                    onChange={(range) => setDateRange(range)}
                                />
                            </FilterDateField>

                            <FilterField>
                                <FieldLabel>Merchant</FieldLabel>
                                <MerchantFilterCached fullWidth />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Courier</FieldLabel>
                                <RHFSelectField
                                    width="100%" name="courier" label="" placeholder="Select Courier" size="small" displayEmpty
                                    options={_.orderBy(courier, "name").map((val) => ({
                                        value: val.prozoId,
                                        label: val.cpAccountCode,
                                    }))}
                                />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Ticket ID</FieldLabel>
                                <RHFTextField label="" name="ticketId" placeholder="Ticket ID" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>AWB</FieldLabel>
                                <RHFTextField label="" name="awb" placeholder="AWB Number" fullWidth size="small" InputLabelProps={{ shrink: true }} />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Category</FieldLabel>
                                <RHFSelectField
                                    label="" name="category" placeholder="Select Category" width="100%" size="small" displayEmpty
                                    options={_.orderBy(categoryOptions, 'label', 'asc')}
                                />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Subcategory</FieldLabel>
                                <RHFSelectField
                                    label="" name="subcategory" placeholder="Select Subcategory" width="100%" size="small" displayEmpty
                                    options={_.orderBy(subcategoryOptions, "label", "asc") || []}
                                />
                            </FilterField>

                            <FilterField>
                                <FieldLabel>Status</FieldLabel>
                                <RHFSelectField
                                    label="" name="status" placeholder="Select Status" width="100%" size="small" displayEmpty
                                    options={statusList?.filter((val) => status_list.includes(val.id)).map((val) => ({
                                        value: val.id,
                                        label: val.label
                                    }))}
                                />
                            </FilterField>

                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: '0 0 auto' }}>
                                <LoadingButton type="submit" variant="contained" size="small"
                                    sx={{ bgcolor: '#1565c0', color: '#fff', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, '&:hover': { bgcolor: '#0d47a1' } }}>
                                    Filter
                                </LoadingButton>
                                <Button type="button" variant="contained" size="small"
                                    sx={{ bgcolor: '#ef4444', color: '#fff', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, '&:hover': { bgcolor: '#dc2626' } }}
                                    onClick={() => handleReset()}>
                                    Reset
                                </Button>
                            </Box>

                        </Box>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ══════════════ DATA GRID ══════════════ */}
            <Box sx={{
                backgroundColor: '#fff', borderRadius: 3,
                border: '1px solid #e0e0e0', overflow: 'hidden',
                '& .MuiDataGrid-root': { border: 'none' },
                '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f4f6f8',
                    borderBottom: '1px solid #e0e0e0',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                    fontWeight: 700, fontSize: '0.76rem', color: '#637381',
                    textTransform: 'uppercase', letterSpacing: 0.3,
                },
                '& .MuiDataGrid-row': {
                    '&:hover': { backgroundColor: '#f9fafb' },
                },
                '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid #f4f4f4',
                    fontSize: '0.82rem',
                },
                '& .MuiDataGrid-footerContainer': {
                    borderTop: '1px solid #e0e0e0',
                },
            }}>
                <ProzoDataGrid
                    columns={columns}
                    rows={resData?.results?.length > 0 ? resData?.results : []}
                    sizePerPage={sizePerPage}
                    setSizePerPage={setSizePerPage}
                    setPage={setPage}
                    page={page}
                    pagination={true}
                    hideFooterPagination={false}
                    paginationMode={"server"}
                    rowCount={resData?.total || 0}
                    getRowId={(row) => row.id}
                />
            </Box>

            {/* ── Bulk Resolve Modal ── */}
            <MUIModal
                open={openBulkModal}
                hideClose={isBulkResolveProcessing}
                handleClose={() => {
                    if (isBulkResolveProcessing) return; // locked while an upload is running
                    setOpenBulkModal(false)
                    setBulkResolveResults([]);
                    setBulkProgress({ done: 0, total: 0 });
                    reset()
                }}
                title={"Bulk Resolve Tickets"}
            >
                <FormProvider methods={methods} onSubmit={handleSubmit(bulkResolveTickets)}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <RHFFileField name='file' required="true" />
                        </Grid>
                        <Grid item xs={12}>
                            <LoadingButton
                                loading={isBulkResolveProcessing}
                                variant="contained" size="medium"
                                startIcon={<UploadIcon />}
                                type="submit"
                                sx={{ borderRadius: 2, textTransform: 'none' }}
                            >
                                Upload
                            </LoadingButton>
                            <DownloadSampleFile fileName={sampleName} excel={"excel"} />
                        </Grid>
                        {isBulkResolveProcessing && (
                            <Grid item xs={12}>
                                <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#fff8e1', border: '1px solid #ffe082' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#8d6e00' }}>
                                        Upload in progress — do not close, reload, or navigate away, or the remaining tickets won’t be processed.
                                    </Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <LinearProgress variant="determinate" value={bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0} />
                                        <Typography variant="caption" sx={{ color: '#8d6e00' }}>{bulkProgress.done} / {bulkProgress.total} processed</Typography>
                                    </Box>
                                    <Button size="small" color="error" variant="outlined" sx={{ mt: 1, textTransform: 'none' }} onClick={() => { cancelBulkRef.current = true; }}>
                                        Stop upload
                                    </Button>
                                </Box>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            {bulkResolveResults.map((result, index) => (
                                <Box key={index} sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    py: 0.75, px: 1.5, mb: 0.5, borderRadius: 1.5,
                                    backgroundColor: result.status === 'Success' ? '#e8f5e9' : '#fce4ec',
                                }}>
                                    <Typography variant="body2" sx={{
                                        fontWeight: 600, fontSize: '0.8rem',
                                        color: result.status === 'Success' ? '#2e7d32' : '#c62828',
                                    }}>
                                        Ticket #{result.ticketId}: {result.message}
                                    </Typography>
                                </Box>
                            ))}
                        </Grid>
                    </Grid>
                </FormProvider>
            </MUIModal>

            {/* ── Bulk Reply Modal ── */}
            <MUIModal
                open={openBulkReplyModal}
                hideClose={isBulkReplyProcessing}
                handleClose={() => {
                    if (isBulkReplyProcessing) return; // locked while an upload is running
                    setOpenBulkReplyModal(false)
                    setBulkReplyResults([]);
                    setBulkProgress({ done: 0, total: 0 });
                    reset()
                }}
                title={"Bulk Add Comments"}
            >
                <FormProvider methods={methods} onSubmit={handleSubmit(bulkUploadReply)}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <RHFFileField name='file' required="true" />
                        </Grid>
                        <Grid item xs={12}>
                            <LoadingButton
                                loading={isBulkReplyProcessing}
                                variant="contained" size="medium"
                                startIcon={<UploadIcon />}
                                type="submit"
                                sx={{ borderRadius: 2, textTransform: 'none' }}
                            >
                                Upload
                            </LoadingButton>
                            <DownloadSampleFile fileName={sampleName} excel={"excel"} />
                        </Grid>
                        {isBulkReplyProcessing && (
                            <Grid item xs={12}>
                                <Box sx={{ p: 1.5, borderRadius: 1.5, bgcolor: '#fff8e1', border: '1px solid #ffe082' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#8d6e00' }}>
                                        Upload in progress — do not close, reload, or navigate away, or the remaining tickets won’t be processed.
                                    </Typography>
                                    <Box sx={{ mt: 1 }}>
                                        <LinearProgress variant="determinate" value={bulkProgress.total ? (bulkProgress.done / bulkProgress.total) * 100 : 0} />
                                        <Typography variant="caption" sx={{ color: '#8d6e00' }}>{bulkProgress.done} / {bulkProgress.total} processed</Typography>
                                    </Box>
                                    <Button size="small" color="error" variant="outlined" sx={{ mt: 1, textTransform: 'none' }} onClick={() => { cancelBulkRef.current = true; }}>
                                        Stop upload
                                    </Button>
                                </Box>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            {bulkReplyResults.map((result, index) => (
                                <Box key={index} sx={{
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    py: 0.75, px: 1.5, mb: 0.5, borderRadius: 1.5,
                                    backgroundColor: result.status === 'Success' ? '#e8f5e9' : '#fce4ec',
                                }}>
                                    <Typography variant="body2" sx={{
                                        fontWeight: 600, fontSize: '0.8rem',
                                        color: result.status === 'Success' ? '#2e7d32' : '#c62828',
                                    }}>
                                        Ticket #{result.ticketId}: {result.message}
                                    </Typography>
                                </Box>
                            ))}
                        </Grid>
                    </Grid>
                </FormProvider>
            </MUIModal>
        </>
    )
}

export default TicketList


/* ── AWB Details Sub-component ── */
const ViewAwbDetails = ({ id }) => {
    const [order, setOrder] = useState({})
    const [isLoading, setIsLoading] = useState(0)
    const andfilter = [
        {
            "field": "awbNumber",
            "operator": "eq",
            "value": id
        }
    ];
    useEffect(() => {
        setIsLoading(true)
        const exclude = "merchantPricingRTO.merchantRateCard,merchantPricingRTO.pricingObj,courierPricingRTO.pricingObj,courierPricingRTO.rateCard,pickupDetails.pickupTime,pickupDetails.from_email,returnInfo,actualDeliveryTat,invoiceNumber,terminalStatus,shippingCharge,transactionCharge,giftwrapCharge,dispatchMode,proshipDeliveryTat,courierDeliveryTat,itemList.hsn,itemList.itemUrl,itemList.itemDescription,order_history.courierPartnerId,order_history.courierPartnerEdd,merchant,courier,version,orderManifest,overriddenCourierAccountCode,isDiscrepancyRaised,discrepancyId,securityKey,sortCode,channelType,createdByUser,skipRule,modifiedByUser,rvp_reason,customerDetail,cpErrorList,mongoLabelSignedUrlDetails,overriddenCourierRule,mongoInvoiceSignedUrlDetails,merchant,merchantPricing.merchantRateCard,merchantPricing.pricingObj,courierPricing.rateCard,courierPricing.pricingObj,modifiedByUser,lastModifiedDate";
        let d = { "andfilter": andfilter, "excludes": exclude };
        post(apiConstants.ORDER_SEARCH, d)
            .then((res) => {
                setOrder(res.data?.data[0])
                setIsLoading(false)
            }).catch((err) => {
                console.log(err);
                setIsLoading(false)
            })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const fields = [
        { label: 'Shipping Label ID', value: order?.shippingLabelId },
        { label: 'AWB Number', value: order?.awb_number },
        { label: 'Shipment Type', value: order?.order_type },
        { label: 'Current Status', value: order?.orderStatus, isChip: true },
        { label: 'Channel Name', value: order?.channel_name },
        { label: 'Shipping Partner Parent', value: order?.courierDetail?.parent?.includes('Blended') ? order?.actualCourierProviderName : order?.courierDetail?.parent },
        { label: 'Shipping Partner', value: order?.courierDetail?.prozoId },
        { label: 'Last Updated', value: order?.order_history?.length ? parseDateAndTime(order.order_history[order.order_history.length - 1]?.timestamp) : null },
        { label: 'EDD', value: order?.edd ? parseDateAndTime(order.edd) : null },
    ];

    return (
        <>
            {isLoading && <Loader />}
            <Box sx={{
                backgroundColor: '#fff', borderRadius: 3,
                border: '1px solid #e0e0e0', overflow: 'hidden',
            }}>
                <Box sx={{
                    px: 2.5, py: 1.5, backgroundColor: '#f4f6f8',
                    borderBottom: '1px solid #e0e0e0',
                }}>
                    <Typography variant="subtitle2" sx={{
                        fontWeight: 700, color: '#637381',
                        textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.72rem',
                    }}>
                        Order Details
                    </Typography>
                </Box>
                <Grid container sx={{ px: 2.5, py: 2 }}>
                    {fields.map((f, i) => (
                        <Grid item xs={6} md={3} key={i} sx={{ py: 1.5 }}>
                            <Typography variant="caption" sx={{
                                color: '#919EAB', fontWeight: 600,
                                textTransform: 'uppercase', letterSpacing: 0.3,
                                fontSize: '0.68rem', display: 'block', mb: 0.5,
                            }}>
                                {f.label}
                            </Typography>
                            {f.isChip ? (
                                <Chip
                                    label={f.value || '-'}
                                    size="small"
                                    sx={{
                                        height: 24, fontWeight: 600, fontSize: '0.75rem',
                                        backgroundColor: '#e3f2fd', color: '#1565c0',
                                        border: '1px solid #bbdefb',
                                    }}
                                />
                            ) : (
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', color: '#212B36' }}>
                                    {f.value || '-'}
                                </Typography>
                            )}
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </>
    )
}
